import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { notification } from 'antd';
import { makePrioStyles } from '../../../theme/utils';
import Flex from '../../../components/Flex';
import { TimeAndLeaveManagementTimelineItem } from '../selectors';
import TimeKeepingEditTimeRecordForm, {
  TimeRecordFormModel,
} from '../../timeKeeping/components/TimeKeepingEditTimeRecordForm';
import {
  MonthlyCloseId,
  TimeKeepingDayId,
  TimeRecordId,
} from '../../../models/Types';
import { isTemporaryId } from '../../../util';
import {
  CreateTimeRecordRequest,
  TimeRecord,
  UpdateTimeRecordRequest,
} from '../../../models/TimeRecord';
import { useDispatch, useSelector } from 'react-redux';
import { getUserMe } from '../../../apps/main/rootReducer';
import {
  apiCreateTimeRecord,
  apiDeleteTimeRecords,
  apiUpdateTimeRecord,
} from '../../timeRecords/api';
import {
  createTimeRecords,
  deleteMyTimeRecord,
  updateTimeRecord,
} from '../../timeRecords/actions';
import { useTranslation } from 'react-i18next';
import TimeKeepingEditForm, {
  TimeKeepingFormModel,
} from '../../timeKeeping/components/TimeKeepingEditForm';
import {
  apiDeleteTimeKeepingDayMe,
  apiFetchTimeKeepingDaySuggestion,
  apiUpdateTimeKeeping,
} from '../../timeKeeping/api';
import {
  TimeKeepingDay,
  TimeKeepingGeneral,
} from '../../../models/TimeKeeping';
import {
  deleteTimeKeepingDay,
  updateTimeKeepingDay,
} from '../../timeKeeping/actions';
import moment from 'moment';
import { toISOString } from '../../timeKeeping/utils';

const useStyles = makePrioStyles((theme) => ({
  root: {
    overflow: 'hidden',
    position: 'relative',
    '&::before': {
      content: "''",
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: 0,
      width: 8,
      backgroundColor: theme.old.palette.backgroundPalette.base,
    },
  },
  form: {
    overflow: 'auto',
    height: '100%',
    width: '100%',
    padding: theme.old.spacing.defaultPadding,
  },
}));

interface TimeAndLeaveTimelineFormManagerProps {
  className?: string;
  selectedItem: TimeAndLeaveManagementTimelineItem;
  timeKeepingGeneralSettings: TimeKeepingGeneral | null;
  onFinish: (
    id: string,
    item: TimeAndLeaveManagementTimelineItem,
    type: 'timeKeeping' | 'timeRecord'
  ) => void;
  onCancel: (
    item: TimeAndLeaveManagementTimelineItem,
    type: 'timeKeeping' | 'timeRecord'
  ) => void;
  onChange: (
    item: TimeAndLeaveManagementTimelineItem,
    type: 'timeKeeping' | 'timeRecord'
  ) => void;
  onDelete: (
    id: string,
    groupId: string,
    type: 'timeKeeping' | 'timeRecord'
  ) => void;
}

export const TimeAndLeaveTimelineFormManager: React.FC<
  TimeAndLeaveTimelineFormManagerProps
> = (props) => {
  //#region ------------------------------ Defaults
  const {
    className,
    selectedItem,
    timeKeepingGeneralSettings,
    onFinish,
    onCancel,
    onChange,
    onDelete,
  } = props;
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const userMe = useSelector(getUserMe);

  const [loading, setLoading] = useState<boolean>(false);
  const [formResetKey, setFormResetKey] = useState<number>(0);
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const handleOnTimeRecordCreation = useCallback(
    async (value: TimeRecordFormModel, timeRecordId: TimeRecordId) => {
      if (isTemporaryId(timeRecordId)) {
        setLoading(true);
        const request: CreateTimeRecordRequest = {
          projectId: selectedItem.groupId,
          description: value.description,
          day: selectedItem.startDateTime.split('T')[0],
          contactId: userMe.id,
          kilometerDistance: value.kilometerDistance ?? 0,
          title: value.title,
          timeRecordEntries: selectedItem.entries,
          projectPhaseId: value.projectPhaseId,
          isSeparateService: value.isSeparateService,
        };
        await apiCreateTimeRecord([request], request.projectId, true)
          .then(({ data }) => {
            const timeRecord = data[0];
            const sortedEntries = timeRecord.timeRecordEntries.sort((a, b) =>
              moment(a.startTime).isBefore(moment(b.startTime)) ? -1 : 1
            );
            dispatch(createTimeRecords(data));
            onFinish(
              timeRecordId,
              {
                id: timeRecord.timeRecordId,
                groupId: timeRecord.projectId,
                title: timeRecord.title,
                type: 'timeRecord',
                startDateTime: toISOString(sortedEntries[0].startTime),
                endDateTime: toISOString(
                  sortedEntries[sortedEntries.length - 1].endTime
                ),
                entries: timeRecord.timeRecordEntries,
                value: timeRecord,
                state: 'ok',
              },
              'timeRecord'
            );
            apiFetchTimeKeepingDaySuggestion(
              toISOString(timeRecord.timeRecordEntries[0].startTime),
              toISOString(
                timeRecord.timeRecordEntries[
                  timeRecord.timeRecordEntries.length - 1
                ].endTime
              )
            );
          })
          .catch((error) => {
            notification.open({
              message: t('common:error'),
              description: t('timeRecords:errorMessages.createError'),
            });
          });

        setLoading(false);
      }
    },
    [selectedItem, userMe, dispatch, onFinish, t]
  );

  const handleOnTimeRecordChange = useCallback(
    async (
      value: TimeRecordFormModel,
      item: TimeAndLeaveManagementTimelineItem
    ) => {
      let newItem: TimeAndLeaveManagementTimelineItem = {
        ...item,
        value: {
          ...item.value,
          ...value,
        },
      };
      const _value: TimeRecord = newItem.value as TimeRecord;

      const request: UpdateTimeRecordRequest = {
        timeRecordId: _value.timeRecordId,
        projectId: _value.projectId,
        subProjectId: _value.subProjectId,
        description: _value.description,
        day: _value.day,
        contactId: _value.contactId,
        kilometerDistance: _value.kilometerDistance ?? 0,
        title: _value.title,
        timeRecordEntries: _value.timeRecordEntries,
        rowVersion: _value.rowVersion,
        projectPhaseId: _value.projectPhaseId,
        isSeparateService: _value.isSeparateService,
      };

      const { data } = await apiUpdateTimeRecord(
        request,
        _value.timeRecordId,
        _value.rowVersion,
        'me'
      );
      if (data) {
        const sortedEntries = data.timeRecordEntries.sort((a, b) =>
          moment(a.startTime).isBefore(moment(b.startTime)) ? -1 : 1
        );
        dispatch(
          updateTimeRecord(data, data.timeRecordId, item.value as TimeRecord)
        );
        onChange(
          {
            id: data.timeRecordId,
            groupId: data.projectId,
            title: data.title,
            type: 'timeRecord',
            startDateTime: toISOString(sortedEntries[0].startTime),
            endDateTime: toISOString(
              sortedEntries[sortedEntries.length - 1].endTime
            ),
            entries: data.timeRecordEntries,
            value: data,
            state: 'ok',
          },
          'timeRecord'
        );
      } else {
        notification.open({
          message: t('common:error'),
          description: t('timeRecords:errorMessages.updateError'),
        });
      }
    },
    [onChange, dispatch, t]
  );

  const handleOnTimeRecordFormFinish = useCallback(
    (
      value: TimeRecordFormModel,
      timeRecord: TimeAndLeaveManagementTimelineItem
    ) => {
      if (isTemporaryId(timeRecord.id)) {
        handleOnTimeRecordCreation(value, timeRecord.id);
      } else {
        handleOnTimeRecordChange(value, timeRecord);
      }
    },
    [handleOnTimeRecordChange, handleOnTimeRecordCreation]
  );

  const handleOnTimeRecordFormCancel = useCallback(
    (item: TimeAndLeaveManagementTimelineItem) => {
      onCancel(item, 'timeRecord');
    },
    [onCancel]
  );

  const handleOnTimeRecordDelete = useCallback(
    async (timeRecordId: TimeRecordId) => {
      setLoading(true);
      const { result } = await apiDeleteTimeRecords(
        timeRecordId,
        selectedItem?.value as TimeRecord,
        'me'
      );
      if (result.status >= 200 && result.status < 300) {
        dispatch(
          deleteMyTimeRecord(
            timeRecordId,
            selectedItem?.value as TimeRecord,
            true
          )
        );
        onDelete(timeRecordId, selectedItem.groupId, 'timeRecord');
      }
      setLoading(false);
    },
    [onDelete, selectedItem, dispatch]
  );

  const handleOnTimeKeepingChange = useCallback(
    async (
      value: TimeKeepingFormModel,
      item: TimeAndLeaveManagementTimelineItem
    ) => {
      setLoading(true);

      // Store original values for rollback on error
      const originalItem = { ...item };

      const timeFields: (keyof TimeKeepingFormModel)[] = [
        'fromHour',
        'fromMinute',
        'toHour',
        'toMinute',
        'breakHour',
        'breakMinute',
      ];
      const hasTimeChanges = timeFields.some((f) => value[f] !== undefined);

      let updatedEntries = [...(item.entries || [])];

      if (hasTimeChanges) {
        const baseDate = moment(
          updatedEntries[0]?.startTime || item.startDateTime
        );
        const dateString = baseDate.toISOString(true).split('T')[0];

        if (value.fromHour !== undefined && value.fromMinute !== undefined) {
          updatedEntries[0] = {
            ...updatedEntries[0],
            startTime: `${dateString}T${String(value.fromHour).padStart(2, '0')}:${String(value.fromMinute).padStart(2, '0')}:00`,
          };
        }

        if (value.toHour !== undefined && value.toMinute !== undefined) {
          updatedEntries[updatedEntries.length - 1] = {
            ...updatedEntries[updatedEntries.length - 1],
            endTime: `${dateString}T${String(value.toHour).padStart(2, '0')}:${String(value.toMinute).padStart(2, '0')}:00`,
          };
        }

        // Handle break duration (only for days with 1-2 entries)
        const originalEntries = item.entries || [];
        const breakMinutes =
          (value.breakHour ?? 0) * 60 + (value.breakMinute ?? 0);
        const startTime = moment(updatedEntries[0]?.startTime);
        const endTime = moment(
          updatedEntries[updatedEntries.length - 1]?.endTime
        );
        const totalMinutes = endTime.diff(startTime, 'minutes');
        const fmt = (m: moment.Moment) =>
          `${dateString}T${m.format('HH:mm:ss')}`;

        if (originalEntries.length <= 2) {
          const minWorkSegment = 1;
          if (breakMinutes > 0) {
            const hadBreak = originalEntries.length >= 2;
            let breakStart = hadBreak
              ? moment.min(
                  moment(originalEntries[0].endTime),
                  moment(endTime).subtract(
                    breakMinutes + minWorkSegment,
                    'minutes'
                  )
                )
              : moment(startTime).add(
                  Math.floor((totalMinutes - breakMinutes) / 2),
                  'minutes'
                );
            breakStart = moment.max(
              breakStart,
              moment(startTime).add(minWorkSegment, 'minutes')
            );
            const breakEnd = moment(breakStart).add(breakMinutes, 'minutes');

            if (
              breakEnd.isSameOrAfter(
                moment(endTime).subtract(minWorkSegment, 'minutes')
              )
            ) {
              setFormResetKey((prev) => prev + 1);
              notification.open({
                message: t('common:error'),
                description: t(
                  'timeKeeping:messages.errorMessages.breakExceedsWorkTime'
                ),
              });
              setLoading(false);
              return;
            }

            const {
              startTime: _s,
              endTime: _e,
              ...baseEntry
            } = updatedEntries[0];
            updatedEntries = [
              {
                ...updatedEntries[0],
                startTime: fmt(startTime),
                endTime: fmt(breakStart),
              },
              {
                ...baseEntry,
                ...(updatedEntries[1] ?? {}),
                startTime: fmt(breakEnd),
                endTime: fmt(endTime),
              },
            ];
          } else {
            updatedEntries = [
              {
                ...updatedEntries[0],
                startTime: fmt(startTime),
                endTime: fmt(endTime),
              },
            ];
          }
        }
      }

      const newItem: TimeAndLeaveManagementTimelineItem = {
        ...item,
        value: {
          ...item.value,
          type: value.type,
          notes: value.notes,
        },
      };

      const { data } = await apiUpdateTimeKeeping(item.id, {
        type: (newItem.value as TimeKeepingDay).type,
        notes: (newItem.value as TimeKeepingDay).notes,
        timeKeepingEntries: updatedEntries,
      });

      if (data) {
        const sortedEntries = data.timeKeepingEntries.sort((a, b) =>
          moment(a.startTime).isBefore(moment(b.startTime)) ? -1 : 1
        );
        dispatch(updateTimeKeepingDay(data, item.id));
        onChange(
          {
            id: data.timeKeepingDayId,
            groupId: item.groupId,
            title: '',
            type: 'timeKeeping',
            startDateTime: sortedEntries[0].startTime,
            endDateTime: sortedEntries[sortedEntries.length - 1].endTime,
            entries: data.timeKeepingEntries,
            value: data,
            state: 'ok',
            notes: data.notes,
          } as TimeAndLeaveManagementTimelineItem,
          'timeKeeping'
        );
      } else {
        // Rollback to original values on error - force form remount
        setFormResetKey((prev) => prev + 1);
        onChange(originalItem, 'timeKeeping');
        notification.open({
          message: t('common:error'),
          description: t('timeKeeping:messages.errorMessages.updateError'),
        });
      }
      setLoading(false);
    },
    [onChange, dispatch, t]
  );

  const handleOnTimeKeepingDelete = useCallback(
    async (
      monthlyCloseId: MonthlyCloseId,
      timeKeepingDayId: TimeKeepingDayId
    ) => {
      setLoading(true);
      const { result } = await apiDeleteTimeKeepingDayMe(timeKeepingDayId);

      if (result.status >= 200 && result.status < 300) {
        dispatch(deleteTimeKeepingDay(monthlyCloseId, timeKeepingDayId));
        onDelete(timeKeepingDayId, selectedItem.groupId, 'timeKeeping');
      }
      setLoading(false);
    },
    [selectedItem, onDelete, dispatch]
  );
  //#endregion

  //#region ------------------------------ Components
  const content = useMemo(() => {
    switch (selectedItem.type) {
      case 'timeKeeping': {
        return (
          <TimeKeepingEditForm
            key={`timeKeeping-${selectedItem.id}-${formResetKey}`}
            className={classes.form}
            selectedItem={selectedItem}
            onChange={handleOnTimeKeepingChange}
            onDelete={handleOnTimeKeepingDelete}
            loading={loading}
            timeKeepingGeneralSettings={timeKeepingGeneralSettings}
          />
        );
      }
      case 'timeRecord': {
        return (
          <TimeKeepingEditTimeRecordForm
            className={classes.form}
            selectedItem={selectedItem}
            onCancel={handleOnTimeRecordFormCancel}
            onFinish={handleOnTimeRecordFormFinish}
            onDelete={handleOnTimeRecordDelete}
            loading={loading}
          />
        );
      }
      default: {
        return null;
      }
    }
  }, [
    loading,
    selectedItem,
    classes,
    handleOnTimeRecordFormCancel,
    handleOnTimeRecordFormFinish,
    handleOnTimeRecordDelete,
    handleOnTimeKeepingChange,
    handleOnTimeKeepingDelete,
    formResetKey,
    timeKeepingGeneralSettings,
  ]);
  //#endregion

  //#region ------------------------------ Effects
  useEffect(() => {
    if (selectedItem) {
      setLoading(false);
    }
  }, [selectedItem]);
  //#endregion

  return (
    <Flex.Item className={classNames(classes.root, className)}>
      {content}
    </Flex.Item>
  );
};

export default TimeAndLeaveTimelineFormManager;
